<template>
    <div class="font-body bg-white">
        <!-- ==================== LANDING NAV ==================== -->
        <LandingNav
            background-color="#FFFFFF"
            logo-light="/hkcm/images/logo-on-light-background.svg"
            logo-small-light="/hkcm/images/logo-small-on-light-background.svg"
            :cta-text="'Analysen entdecken'"
            cta-link="/produkte"
            button-gradient-from="#006CFF"
            button-gradient-to="#006CFF"
            :show-ticker="true"
            ticker-text="+++ Ø<span class='font-semibold'> 70 % Trefferquote</span> über alle Analysebereiche +++ <span class='font-semibold'>Klare</span> Einstiegsbereiche, Zielzonen und Szenarien +++ <span class='font-semibold'>Strukturierte Szenarien</span> statt Bauchgefühl +++"
        />

        <!-- HERO -->
        <HeroSection
            background-color="radial-gradient(circle at 50% 50%, #072551 0%, #051A39 50%, #020E20 100%)"
            :badges="[
                { src: '/hkcm/images/partners/BlackBull24_Krypto_square.png', alt: 'Black Bull Award Winner' },
                { src: '/hkcm/images/partners/BlackBull25_InvestmentVision.png', alt: 'Investment Visionary Award' },
                { src: '/hkcm/images/partners/DISQTrust_Siegel_Von Kunden_Empfohlen_HKCM.png', alt: 'DISQ Trust', class: 'w-full object-contain max-h-[180px] lg:w-auto' }
            ]"
            title="ES REICHT!<br>
Benzin wird teurer.<br class='block xl:hidden'> Gas wird teurer.<br>
Alles wird <span style='background: linear-gradient(to top, #E2C750, #E2C750); -webkit-background-clip: text; -webkit-text-fill-color: transparent;'>teurer</span>."
            :title-highlight="['Benzin', 'Gas', 'Alles']"
            title-highlight-gradient-from="#E2C750"
            title-highlight-gradient-to="#E2C750"
            subtitle=" Und die meisten Menschen zahlen einfach, ohne zu verstehen warum.<br>
Verstehen Sie die Märkte dahinter – und nutzen Sie die Entwicklungen zu Ihrem Vorteil, statt darunter zu leiden."
            checkmark-color="text-[#006CFF]"
            cta-text="Sichern Sie sich ab"
            cta-link="/produkte"
            price-color="text-[#E2C750]"
            :social-proof-text="'<div>Bereits über 25.000</div><div>zufriedene Kunden</div>'"
            social-proof-image="/hkcm/images/rezensionenBilder.png"
            :app-store-data="{
                link: 'https://apps.apple.com/de/app/hkcm/id6499010943',
                badge: i18next.language === 'de' ? '/hkcm/images/apple-store-badge.png' : '/hkcm/images/apple-store-badge-en.png',
                rating: '4.4',
                stars: '/hkcm/images/stars4_5_yellow.png',
                reviews: '1.8k'
            }"
            :google-play-data="{
                link: 'https://play.google.com/store/apps/details?id=de.hkcm.app&hl=de&pli=1',
                badge: i18next.language === 'de' ? '/hkcm/images/google-play-badge.png' : '/hkcm/images/google-play-badge-en.png',
                rating: '4.5',
                stars: '/hkcm/images/stars4_5_green.png',
                reviews: '7.3k'
            }"
            background-image="/hkcm/images/bg_candels.png"
            button-gradient-from="#93802F"
            button-gradient-to="#E2C750"
        />

        <!-- VIDEO SECTION -->
        <VideoSection
            background-color="transparent"
            video-url="https://iframe.mediadelivery.net/embed/369702/8dd6cbcb-8aef-4d56-bea9-4a9546f5e734?autoplay=false&loop=false&muted=false&preload=true&responsive=true"
            :video-height="500"
            discount-text="<span class='hidden xl:block'>Rabatt</span> <br class='hidden'><span class='text-[20px]'>nur bis 06.05.2026</span>"
            discount-percentage="20% <span class='text-[24px] xl:hidden'>Rabatt</span>"
            discount-badge-color="#006CFF"
            :show-discount="true"
        />

        <!-- ==================== 3 KACHELN SECTION ==================== -->
        <section class="px-5 lg:px-[120px] py-[80px] lg:py-[150px]">
            <div class="max-w-[1200px] mx-auto flex flex-col items-center gap-[30px] lg:gap-[50px]">
                <!-- Title -->
                <h2 class="text-black font-semibold text-[28px] leading-[36.4px] lg:text-[44px] lg:leading-[57.2px] font-body text-left w-full">
                    Ihr Zugang zu den <span class="bg-gradient-to-b from-[#93802F] to-[#E2C750] bg-clip-text text-transparent">Energiemärkten</span>.<br class="block xl:hidden" />
                    Wählen Sie jetzt Ihr Analyse-Paket.
                </h2>

                <!-- Cards -->
                <div class="flex flex-col lg:flex-row gap-5 w-full">
                    <!-- Card 1: Rohöl-Analyse -->
                    <div class="flex-1 bg-[#E5F0FF] rounded-[20px] p-[30px] flex flex-col gap-5">
                        <h3 class="text-black font-semibold text-[22px] leading-[28.6px] lg:text-[24px] lg:leading-[31.2px] font-body text-center">Rohöl-Analyse</h3>
                        <div class="w-[70px] h-[70px] mx-auto">
                            <img :src="$asset('/hkcm/images/landing/hyper/icon-target.png', 'public')" alt="Target" class="w-full h-full object-contain" />
                        </div>
                        <p class="text-black font-pro text-[18px] leading-[23.4px] text-center">Strukturierte Einordnung des wichtigsten Energiemarktes der Welt.</p>
                    </div>
                    <!-- Card 2: Erdgas-Analyse -->
                    <div class="flex-1 bg-[#E5F0FF] rounded-[20px] p-[30px] flex flex-col gap-5">
                        <h3 class="text-black font-semibold text-[22px] leading-[28.6px] lg:text-[24px] lg:leading-[31.2px] font-body text-center">Erdgas-Analyse</h3>
                        <div class="w-[70px] h-[70px] mx-auto">
                            <img :src="$asset('/hkcm/images/landing/hyper/icon-trendup.png', 'public')" class="w-full h-full object-contain" alt="TrendUp" />
                        </div>
                        <p class="text-black font-pro text-[18px] leading-[23.4px] text-center">
                            Verstehen Sie die Dynamik eines zentralen Energieträgers – fundiert, aktuell, strukturiert.
                        </p>
                    </div>
                    <!-- Card 3: Videoanalysen -->
                    <div class="flex-1 bg-[#E5F0FF] rounded-[20px] p-[30px] flex flex-col gap-5">
                        <h3 class="text-black font-semibold text-[22px] leading-[28.6px] lg:text-[24px] lg:leading-[31.2px] font-body text-center">Videoanalysen</h3>
                        <div class="w-[70px] h-[70px] mx-auto">
                            <img :src="$asset('/hkcm/images/landing/hyper/icon-videocamera.png', 'public')" alt="VideoCamera" class="w-full h-full object-contain" />
                        </div>
                        <p class="text-black font-pro text-[18px] leading-[23.4px] text-center">
                            Komplexe Marktbewegungen visuell erklärt – ideal für alle die lieber schauen, als lesen.
                        </p>
                    </div>
                </div>

                <!-- CTA Button -->
                <router-link
                    to="/produkte"
                    class="flex items-center justify-center gap-[10px] w-full lg:w-auto px-5 py-5 rounded-full font-semibold text-[20px] leading-[24.38px] text-white transition hover:opacity-90 font-body"
                    style="background: linear-gradient(to bottom right, #93802f, #e2c750)"
                >
                    Jetzt Zugang sichern
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                </router-link>
            </div>
        </section>

        <!-- ==================== TITEL, TEXT, TITEL, TEXT SECTION ==================== -->
        <section class="bg-[#006CFF10] px-5 lg:px-[120px] py-[80px] lg:py-[150px]">
            <div class="max-w-[1200px] mx-auto flex flex-col justify-center gap-[50px] lg:gap-[100px]">
                <!-- Block 1 -->
                <div class="flex flex-col gap-[30px] lg:gap-[50px]">
                    <h2 class="text-black font-semibold text-[28px] leading-[36.4px] lg:text-[44px] lg:leading-[57.2px] text-center font-body">
                        Sie zahlen die <span class="bg-gradient-to-b from-[#93802F] to-[#E2C750] bg-clip-text text-transparent">Rechnung</span>. Andere verstehen den Markt.
                    </h2>
                    <p class="text-black font-pro text-[18px] leading-[23.4px] text-center">
                        Energie, Lebensmittel, Mobilität – alles wird teurer. Und viele Menschen haben das Gefühl, nur noch zu reagieren, statt selbst zu entscheiden.<br /><br />Das
                        Problem sind nicht die Preise. Es ist das fehlende Verständnis für die Mechanismen dahinter. Wer nicht versteht, was passiert, kann auch nicht souverän
                        handeln und fundierte Investmententscheidungen treffen.
                    </p>
                </div>
                <!-- Block 2 -->
                <div class="flex flex-col gap-[30px] lg:gap-[50px]">
                    <h2 class="text-black font-semibold text-[28px] leading-[36.4px] lg:text-[44px] lg:leading-[57.2px] text-center font-body">
                        Sie spüren die <span class="bg-gradient-to-b from-[#93802F] to-[#E2C750] bg-clip-text text-transparent">Auswirkungen</span>. Aber niemand erklärt Ihnen die
                        Ursache.
                    </h2>
                    <p class="text-black font-pro text-[18px] leading-[23.4px] text-center">
                        Globale Ereignisse, geopolitische Spannungen und wirtschaftliche Entwicklungen – sie alle beeinflussen direkt die Preise, die Sie täglich zahlen. Doch die
                        meisten Informationen sind widersprüchlich, oberflächlich oder kurzfristig gedacht.<br /><br />Was fehlt, ist ein klarer Blick auf das große Ganze. Eine
                        Struktur, die Ihnen hilft, von Entwicklungen zu profitieren, denen andere hilfslos ausgeliefert sind.
                    </p>
                </div>
            </div>
        </section>

        <!-- ==================== BILD LEFT - TEXT RIGHT SECTION ==================== -->
        <section style="background: linear-gradient(135deg, #072551 0%, #020e20 100%)">
            <div class="px-5 xl:px-[120px] py-[80px] xl:py-[150px] flex flex-col xl:flex-row items-center gap-[30px] lg:gap-[50px] justify-center">
                <div class="relative flex-shrink-0">
                    <div
                        class="hidden -top-[150px] left-1/2 transform -translate-x-1/2 absolute xl:flex flex-shrink-0 w-[258px] h-[258px] bg-[#006CFF] rounded-full items-center justify-center px-[30px] py-[20px]"
                    >
                        <p class="text-white text-center font-body">
                            <span class="text-[64px] leading-[83.2px] font-extrabold block">20%</span>
                            <span class="text-[34px] leading-[44.2px] font-semibold block">RABATT</span>
                            <span class="text-[20px] leading-[26px] font-semibold block">nur bis 06.05.2026</span>
                        </p>
                    </div>
                    <img
                        :src="$asset('/hkcm/images/landing/hyper/section5-image.png', 'public')"
                        alt="Märkte verstehen"
                        class="w-full lg:w-[575px] lg:h-[575px] rounded-[10px] object-cover flex-shrink-0"
                    />
                </div>

                <!-- Discount badge - Mobile only -->
                <div class="xl:hidden flex justify-center h-[97px] w-full">
                    <div class="bg-[#006CFF] rounded-full py-[20px] flex items-center justify-center w-full">
                        <p class="text-white text-center font-body">
                            <span class="text-[44px] leading-[90%] font-extrabold block">20% <span class="text-[24px] leading-[31.2px] font-semibold">RABATT</span></span>
                            <span class="text-[20px] leading-[26px] font-semibold block">nur bis 06.05.2026</span>
                        </p>
                    </div>
                </div>

                <div class="flex flex-col gap-[30px] xl:gap-[50px] xl:w-[575px] flex-shrink-0">
                    <!-- Headline: size=44 w=600 lh=57.2 color=#fff align=LEFT "Märkte" = GRADIENT -->
                    <h2 class="text-white font-semibold text-[28px] leading-[36.4px] lg:text-[44px] lg:leading-[57.2px] font-body text-left">
                        Wir machen <span class="bg-gradient-to-b from-[#93802F] to-[#E2C750] bg-clip-text text-transparent">Märkte</span><br class="hidden xl:block" />
                        für Sie verständlich
                    </h2>

                    <div class="text-white font-pro text-[18px] leading-[23.4px] text-left">
                        <p>
                            Unsere Analysen setzen genau dort an, wo Unsicherheit entsteht. Wir zerlegen komplexe Marktbewegungen in nachvollziehbare Strukturen und zeigen Ihnen,
                            welche Entwicklungen möglich sind und warum. Nicht als starre Prognosen. Sondern als klare Szenarien, die Ihnen Orientierung geben.
                        </p>
                        <br />
                        <ul style="list-style-type: disc; padding-left: 20px" class="marker:text-white">
                            <li>
                                <span class="font-semibold">Strukturierte Marktanalysen</span> - Rohöl-, Gas- und Energiemärkte - verständlich aufbereitet und ohne Vorkenntnisse
                                nachvollziehbar.
                            </li>
                            <li>
                                <span class="font-semibold">Globale Einordnung</span> - Verstehen Sie, welche direkten Auswirkungen geopolitische Ereignisse auf Ihren Alltag haben.
                            </li>
                            <li><span class="font-semibold">Klare Szenarien </span> - Mögliche Marktentwicklungen transparent aufbereitet.</li>
                            <li>
                                <span class="font-semibold">Relevante Preisbereiche </span> - Verständlich erklärt, damit Sie Entwicklungen bewusst bewerten und gezielt investieren
                                können.
                            </li>
                        </ul>
                    </div>

                    <!-- Buttons: gap=20, each rounded-full grad=#11d2d4→#0c7475 px=20 py=20 -->
                    <div class="flex flex-col gap-5 items-center xl:items-start">
                        <router-link
                            to="/produkte/rohoel"
                            class="flex items-center justify-center gap-[10px] w-full lg:w-fit px-5 py-5 rounded-full font-semibold text-[20px] leading-[24.38px] text-white transition hover:opacity-90 font-body"
                            style="background: linear-gradient(to bottom, #93802f, #e2c750)"
                        >
                            Zum Rohöl-Analyse-Paket
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </router-link>
                        <router-link
                            to="/produkte/erdgas"
                            class="flex items-center justify-center gap-[10px] w-full lg:w-fit px-5 py-5 rounded-full font-semibold text-[20px] leading-[24.38px] text-white transition hover:opacity-90 font-body"
                            style="background: linear-gradient(to bottom, #93802f, #e2c750)"
                        >
                            Zum Erdgas-Analyse-Paket
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </router-link>
                    </div>
                </div>
            </div>
        </section>

        <!-- ==================== FAQ SECTION ==================== -->
        <section class="bg-white px-5 lg:px-[120px] py-[80px] lg:py-[150px]">
            <div class="max-w-[1200px] mx-auto flex flex-col items-center gap-[30px] lg:gap-[50px]">
                <!-- Title -->
                <h2 class="text-black font-semibold text-[28px] leading-[36.4px] lg:text-[44px] lg:leading-[57.2px] text-center font-body w-full">Häufige Fragen</h2>

                <!-- FAQ items -->
                <div class="flex flex-col gap-5 w-full">
                    <div
                        v-for="(faq, index) in faqs"
                        :key="index"
                        class="rounded-[10px] overflow-hidden transition-all"
                        :class="activeFaq === index ? 'bg-[#E5F0FF]' : 'bg-white'"
                        :style="activeFaq !== index ? 'border: 1px solid #E5E7EB' : ''"
                    >
                        <button class="w-full flex items-center justify-between p-5 text-left gap-5" @click="toggleFaq(index)">
                            <span
                                class="font-semibold text-[22px] leading-[28.6px] lg:text-[24px] lg:leading-[31.2px] font-body"
                                :class="activeFaq === index ? 'text-[#006CFF]' : 'text-black'"
                            >
                                {{ faq.question }}
                            </span>
                            <svg class="w-6 h-6 flex-shrink-0 transition-transform duration-200" :class="{ 'rotate-180': activeFaq === index }" viewBox="0 0 24 24" fill="none">
                                <path d="M6 9L12 15L18 9" :stroke="activeFaq === index ? '#006CFF' : '#000000'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                        <div v-if="activeFaq === index" class="px-5 pb-5">
                            <p class="text-black font-pro text-[18px] leading-[23.4px]">{{ faq.answer }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- FOOTER -->
        <LandingFooter
            background-color="#FFFFFF"
            logo="/hkcm/images/logo-on-light-background.svg"
            logo-link="/produkte"
            link-color="text-[#031837]"
            cta-text="Analysen entdecken"
            cta-link="/produkte"
            button-gradient-from="#006CFF"
            button-gradient-to="#006CFF"
        />
    </div>
</template>

<script>
import i18next from 'i18next';
import HeroSection from '../components/landing/HeroSection.vue';
import LandingNav from '../components/landing/LandingNav.vue';
import VideoSection from '../components/landing/VideoSection.vue';

export default {
    name: 'HyperLandingPage',
    components: { VideoSection, HeroSection, LandingNav },
    data() {
        return {
            showVideoModal: false,
            activeFaq: 0,
            faqs: [
                {
                    question: 'Für wen sind die Analysen geeignet und brauche ich Vorkenntnisse?',
                    answer: 'Die Inhalte richten sich an alle, die steigende Preise und Marktbewegungen besser verstehen möchten – unabhängig von Vorkenntnissen. Unsere Analysen sind so aufgebaut, dass Sie die Inhalte auch ohne Erfahrung nachvollziehen können. Wir arbeiten mit klaren Strukturen, visuellen Darstellungen und verständlichen Erklärungen – Schritt für Schritt. Gleichzeitig bieten die Analysen auch für erfahrene Nutzer eine fundierte Grundlage zur Einordnung von Marktbewegungen.'
                },
                {
                    question: 'Was unterscheidet diese Analysen von Nachrichten oder kostenlosen Inhalten?',
                    answer: 'Nachrichten liefern einzelne Informationen und Ereignisse. Unsere Analysen ordnen diese ein, zeigen Zusammenhänge und machen Entwicklungen nachvollziehbar. Der Fokus liegt nicht auf Schlagzeilen, sondern auf dem Gesamtbild.'
                },
                {
                    question: 'Sind das konkrete Kauf- oder Verkaufsempfehlungen?',
                    answer: 'Nein. Es werden keine pauschalen Signale geliefert. Stattdessen zeigen die Analysen, wann Märkte attraktiv oder risikoreich werden. Sie identifizieren relevante Marktphasen und Zielzonen – beispielsweise für Rohöl oder Gas. Dadurch können Ein- und Ausstiege eigenständig besser geplant und gezielter getimt werden.'
                },
                {
                    question: 'Was genau bekomme ich nach dem Kauf?',
                    answer: 'Sie erhalten Zugang zu Rohstoffanalysen rund um Rohöl- und/oder Gasmärkte sowie – je nach Auswahl – ergänzende Videoanalysen.  Zusätzlich erhalten Sie regelmäßige Updates bei relevanten Marktbewegungen, Zugriff über Dashboard, App und E-Mail sowie eine strukturierte Aufbereitung der Analysen.  So können Sie die Inhalte flexibel und in Ihrem eigenen Tempo nutzen.'
                },
                {
                    question: 'Wie helfen mir die Analysen konkret im Alltag?',
                    answer: 'Steigende Preise betreffen viele Lebensbereiche – von Energie über Mobilität bis hin zu Lebensmitteln. Unsere Analysen helfen Ihnen, die Hintergründe dieser Entwicklungen besser zu verstehen und Marktbewegungen einzuordnen. Das Ziel ist nicht, kurzfristige Entscheidungen zu erzwingen, sondern Ihnen eine fundierte Grundlage zu geben, um Entwicklungen bewusster zu bewerten.'
                },
                {
                    question: 'Wie oft erhalte ich neue Inhalte oder Updates?',
                    answer: 'Sie erhalten regelmäßig neue Analysen und Updates – immer dann, wenn sich relevante Marktbewegungen ergeben. Der Fokus liegt nicht auf möglichst vielen Updates, sondern auf den Entwicklungen, die aus Marktsicht wirklich relevant sind.'
                },
                {
                    question: 'Wie kann ich die Analysen nutzen?',
                    answer: 'Die Nutzung ist flexibel: direkt im Dashboard am Desktop, unterwegs über die App oder ergänzend per E-Mail.  So entscheiden Sie selbst, wann und wie intensiv Sie sich mit den Inhalten beschäftigen.'
                },
                {
                    question: 'Wie lange habe ich Zugriff und kann ich kündigen?',
                    answer: 'Der Zugriff richtet sich nach dem gewählten Paket und beträgt zwischen einem und zwölf Monaten. Während dieser Zeit stehen Ihnen alle Inhalte uneingeschränkt zur Verfügung, inklusive laufender Updates. Die Kündigung ist jederzeit möglich und wird mit einer Frist von fünf Tagen zum Ende der jeweiligen Laufzeit wirksam. Erfolgt keine Verlängerung, endet der Zugriff automatisch. Alle relevanten Informationen zu Laufzeit, Kündigung und aktuellem Status finden Sie jederzeit transparent in Ihrem Kundenbereich.'
                },
                {
                    question: 'Wie viel kostet der Zugang?',
                    answer: 'Die genauen Preise und verfügbaren Pakete sehen Sie direkt im nächsten Schritt. Es gibt keine versteckten Kosten oder zusätzlichen Gebühren.'
                },
                {
                    question: 'Warum gibt es aktuell 20 % Rabatt?',
                    answer: 'Wir befinden uns aktuell in einer Phase steigender Preise und wirtschaftlicher Unsicherheit. Mit der Aktion möchten wir den Einstieg bewusst erleichtern und einen Anreiz schaffen, sich intensiver mit den Hintergründen dieser Entwicklungen auseinanderzusetzen.'
                },
                {
                    question: 'Wie schnell bekomme ich Zugriff?',
                    answer: 'Direkt nach Ihrer Buchung erhalten Sie Zugang zu allen Inhalten. Sie können sofort starten – ohne Wartezeit.'
                }
            ]
        };
    },
    methods: {
        i18next() {
            return i18next;
        },
        openVideo() {
            this.showVideoModal = true;
        },
        toggleFaq(index) {
            this.activeFaq = this.activeFaq === index ? null : index;
        }
    }
};
</script>
